import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, Star, MessageSquare, ShieldCheck } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

const initials = (s = "") => s.split(/\s+/).map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
};

const StarRating = ({ value, onChange, size = 18, testId }) => (
    <div data-testid={testId} className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <button
                key={n}
                type="button"
                onClick={() => onChange?.(n)}
                aria-label={`Rate ${n} stars`}
                data-testid={`${testId}-${n}`}
                className={`transition-transform hover:scale-110 ${onChange ? "cursor-pointer" : "cursor-default"}`}
            >
                <Star
                    size={size}
                    className={n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}
                />
            </button>
        ))}
    </div>
);

/** Shared review panel for a candidate/pipeline entry.
 * Employer + hiring_team users can both use this. */
export const CandidateReviewPanel = ({ pipelineId }) => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [myRating, setMyRating] = useState(0);
    const [ratingNote, setRatingNote] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/pipeline/${pipelineId}/comments`);
            setData(data);
            const mine = data.ratings?.find((r) => r.author_id === user?.user_id);
            if (mine) { setMyRating(mine.rating); setRatingNote(mine.note || ""); }
        } catch (err) { toast.error(formatApiError(err)); }
    }, [pipelineId, user]);
    useEffect(() => { load(); }, [load]);

    const submitComment = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        try {
            setSending(true);
            await api.post(`/pipeline/${pipelineId}/comments`, { body });
            setBody("");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    const rate = async (n) => {
        try {
            await api.post(`/pipeline/${pipelineId}/rating`, { rating: n, note: ratingNote });
            setMyRating(n);
            toast.success(`Rated ${n}/5.`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!data) return <div className="py-8 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid={`review-panel-${pipelineId}`} className="space-y-5">
            {/* Ratings */}
            <div className="card-soft p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h4 className="font-heading font-semibold text-foreground flex items-center gap-2"><Star size={15} className="text-amber-400" /> Team ratings</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {data.ratings.length > 0
                                ? `Average ${data.avg_rating?.toFixed(1)}/5 across ${data.ratings.length} rating${data.ratings.length === 1 ? "" : "s"}`
                                : "No ratings yet — be the first."}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Your rating</div>
                        <StarRating value={myRating} onChange={rate} testId={`rate-${pipelineId}`} />
                    </div>
                </div>
                {data.ratings.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {data.ratings.map((r) => (
                            <div key={r.id} className="flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs">
                                <Avatar className="w-5 h-5"><AvatarFallback className="bg-brand-navy text-white text-[9px]">{initials(r.author_name || "")}</AvatarFallback></Avatar>
                                <span className="text-foreground font-medium">{r.author_name || "Teammate"}</span>
                                <StarRating value={r.rating} size={11} testId={`rating-display-${r.id}`} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Comments */}
            <div className="card-soft p-5">
                <h4 className="font-heading font-semibold text-foreground flex items-center gap-2 mb-4"><MessageSquare size={15} className="text-brand-green" /> Comments ({data.comments.length})</h4>
                {data.comments.length === 0 ? (
                    <div className="text-sm text-muted-foreground pb-4">No comments yet. Start the conversation.</div>
                ) : (
                    <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                        {data.comments.map((c) => (
                            <motion.div key={c.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} data-testid={`comment-${c.id}`} className="flex gap-3">
                                <Avatar className="w-8 h-8 shrink-0"><AvatarFallback className="bg-brand-navy text-white text-[10px] font-bold">{initials(c.author_name || "")}</AvatarFallback></Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-foreground text-sm">{c.author_name || "Teammate"}</span>
                                        {c.author_role && <Badge variant="outline" className="text-[10px] py-0">{c.author_role === "hiring_team" ? "Team" : c.author_role}</Badge>}
                                        <span className="text-[10px] text-muted-foreground">{timeAgo(c.created_at)}</span>
                                    </div>
                                    <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{c.body}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
                <form onSubmit={submitComment} className="flex gap-2 items-end">
                    <Textarea
                        rows={2}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Add a comment for the team…"
                        data-testid={`comment-input-${pipelineId}`}
                        className="resize-none"
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitComment(e); } }}
                    />
                    <button
                        type="submit"
                        disabled={sending || !body.trim()}
                        data-testid={`comment-send-${pipelineId}`}
                        className="btn-green shrink-0 disabled:opacity-50"
                    >
                        {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    </button>
                </form>
                <p className="mt-2 text-[10px] text-muted-foreground flex items-center gap-1"><ShieldCheck size={10} /> Only your hiring team can see these comments.</p>
            </div>
        </div>
    );
};

export default CandidateReviewPanel;
